import React, { useState, useEffect, useContext } from 'react';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { Button } from '~/shadcn/components/ui/Button';
import { Card, CardContent } from '~/shadcn/components/ui/Card';
import { Calendar } from '~/shadcn/components/ui/Calendar';
import { Label } from '~/shadcn/components/ui/Label';
import { Popover, PopoverContent, PopoverTrigger } from '~/shadcn/components/ui/Popover';
import { HandsontableDataGrid } from '~/shared/components/dataTableHandson';
import { ExportButton } from '../../../../shared/components/ExportCSVButton';
import { InventoryAdjustmentApi } from '../../../../api/inventoryAdjustment';
import { cn } from '~/shadcn/lib/utils';
import { Notifications } from '../../../../shared/components/Notifications';
import { UserContext } from '../../../../contexts/UserContext';

const inventoryAdjustmentApi = new InventoryAdjustmentApi();

const HistoricalTab = () => {
    const user = useContext(UserContext);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [historicalEntries, setHistoricalEntries] = useState([]);
    const [totalPendingEntries, setTotalPendingEntries] = useState(0);
    const [totalUnits, setTotalUnits] = useState(0);
    const [totalCost, setTotalCost] = useState(0);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [submitEnabled, setSubmitEnabled] = useState(false);

    const columnHeaders = [
        'Sku',
        'Item Name',
        'Units',
        'Lot Id',
        'Cost (Estimated)',
        'Category',
        'Reason Code',
        'Notes',
        'Created By',
        'Date Entered',
        'Status',
        'Reviewed By',
        'Reviewed On',
    ];

    const columns = [
        { data: 'externalSku', type: 'text', readOnly: true, width: 208.33 },
        { data: 'itemName', type: 'text', readOnly: true, width: 208.33 },
        { data: 'qtyUnits', type: 'numeric', readOnly: true, width: 115.33 },
        { data: 'lotId', type: 'text', readOnly: true, width: 120 },
        {
            data: 'cost',
            type: 'numeric',
            numericFormat: { pattern: '$0,0.00', culture: 'en-US' },
            readOnly: true,
            width: 208.33,
        },
        { data: 'category', type: 'text', readOnly: true, width: 208.33 },
        { data: 'reason', type: 'text', readOnly: true, width: 208.33 },
        { data: 'notes', type: 'text', readOnly: true, width: 208.33 },
        { data: 'createdBy', type: 'text', readOnly: true, width: 208.33 },
        { data: 'createdOn', type: 'date', readOnly: true, width: 208.33 },
        { data: 'status', type: 'text', readOnly: true, width: 208.33 },
        { data: 'reviewedBy', type: 'text', readOnly: true, width: 208.33 },
        { data: 'reviewedOn', type: 'date', readOnly: true, width: 208.33 },
    ];

    const getHistoricalBulkShrinkEntities = async (startDate, endDate) => {
        const facilityId = user.getFacilityID();
        const formattedStartDate = new Date(startDate.setHours(0, 0, 0, 0)).toISOString();
        const formattedEndDate = new Date(endDate.setHours(23, 59, 59, 999)).toISOString();
        console.log('API dates:', formattedStartDate, formattedEndDate);

        setIsLoading(true);
        const result = await inventoryAdjustmentApi.getHistoricalBulkShrinkRequests(
            facilityId,
            formattedStartDate,
            formattedEndDate,
        );
        if (!result.status || !result.data?.rows) {
            setNotification({ text: 'Error fetching historical bulk shrink entities', level: 'error' });
            setIsLoading(false);
            return;
        }
        if (result.data?.rows.length === 0) {
            setNotification({ text: 'No historical bulk shrink entities found for date range', level: 'info' });
            setHistoricalEntries([]);
            setIsLoading(false);
            return;
        }
        setHistoricalEntries(result.data?.rows || []);
        setIsLoading(false);
    };

    useEffect(() => {
        if (startDate && endDate) {
            setSubmitEnabled(true);
        } else {
            setSubmitEnabled(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        setTotalPendingEntries(historicalEntries.length);
        setTotalUnits(historicalEntries.reduce((acc, entry) => acc + entry.qtyUnits, 0));
        setTotalCost(historicalEntries.reduce((acc, entry) => acc + entry.cost, 0));
    }, [historicalEntries]);

    const handleSubmitDates = async () => {
        setIsLoading(true);
        await getHistoricalBulkShrinkEntities(startDate, endDate);
        setIsLoading(false);
    };

    return (
        <div>
            <Notifications options={notification} />
            <div className="w-full max-w-[1200px] mb-4 mx-auto">
                <div className="flex flex-row flex-wrap gap-4 mb-4">
                    <Card className="flex-1">
                        <CardContent>
                            <div className="flex flex-col gap-4 justify-center items-center">
                                <h1 className="text-2xl font-bold">{totalPendingEntries}</h1>
                                <h2 className="text-sm text-gray-500">Approved/Rejected Entries</h2>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="flex-1">
                        <CardContent>
                            <div className="flex flex-col gap-4 justify-center items-center">
                                <h1 className="text-2xl font-bold">{totalUnits}</h1>
                                <h2 className="text-sm text-gray-500">Total Units</h2>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="flex-1">
                        <CardContent>
                            <div className="flex flex-col gap-4 justify-center items-center">
                                <h1 className="text-2xl font-bold">${totalCost.toFixed(2)}</h1>
                                <h2 className="text-sm text-gray-500">Total Cost</h2>
                            </div>
                        </CardContent>
                    </Card>
                </div>
                <div className="flex flex-row justify-between gap-4">
                    <div className="flex gap-4">
                        <Label htmlFor="start-date">Start Date</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    id="start-date"
                                    variant="outline"
                                    className={cn(
                                        'w-[240px] justify-start text-left font-normal',
                                        !startDate && 'text-muted-foreground',
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {startDate ? startDate.toLocaleDateString() : <span>Start Date</span>}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                            </PopoverContent>
                        </Popover>
                        <Label htmlFor="end-date">End Date</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    id="end-date"
                                    variant="outline"
                                    className={cn(
                                        'w-[240px] justify-start text-left font-normal',
                                        !endDate && 'text-muted-foreground',
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {endDate ? endDate.toLocaleDateString() : <span>End Date</span>}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                            </PopoverContent>
                        </Popover>
                        <Button
                            onClick={handleSubmitDates}
                            disabled={!submitEnabled || isLoading}
                            className="min-w-[100px]"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Loading
                                </>
                            ) : (
                                'Search'
                            )}
                        </Button>
                    </div>
                    <div className="flex gap-4">
                        <ExportButton
                            data={historicalEntries}
                            columnHeaders={columnHeaders}
                            columns={columns}
                            filename="shrink-log-historical-entries"
                            disabled={historicalEntries.length === 0}
                        />
                    </div>
                </div>
            </div>
            <div>
                {historicalEntries.length === 0 ? (
                    <Card className="w-full max-w-[1200px] mx-auto">
                        <CardContent className="flex flex-col items-center justify-center py-8">
                            <p className="text-gray-500 text-lg">No historical bulk shrink requests to show</p>
                            <p className="text-gray-400 text-sm mt-2">
                                Select a date range and click Search to view APPROVED and REJECTED bulk shrink requests
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="w-full overflow-x-auto">
                        <HandsontableDataGrid
                            data={historicalEntries}
                            selectable={false}
                            colHeaders={columnHeaders}
                            columns={columns}
                            handsontableSettings={{
                                manualColumnResize: true,
                                autoColumnSize: false,
                                rowHeaders: false,
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export { HistoricalTab };
